import { memo } from 'react';
import { motion } from 'motion/react';
import type { Project } from '@/api/client';
import { cn } from '../../cn';
import { EmptyValue } from '../../ui/empty-value';
import { useListItemMotion } from '../../ui/list-motion';
import { Tooltip } from '../../ui/tooltip';
import { NameNote } from '../parts/NameNote';
import { ProjectCover } from '../parts/ProjectCover';
import { ProjectFlagDots } from '../parts/ProjectFlagDots';
import { ProjectTypeMark } from '../parts/ProjectTypeMark';
import { StageChip } from '../parts/StageChip';
import { StageSwatch } from '../parts/StageSwatch';
import { DEADLINE_TEXT_TONE, deadlineLeftLabel, deadlineTooltipLabel } from '../parts/deadlineText';
import { getListRowView, type ListDeadlineView, type ListRowOptions } from './listRowView';
import { projectChatDragProps } from './projectChatDrag';
import { HIGHLIGHTED } from './rowHighlight';

/**
 * Строка списка карточкой (концепт compact-rows 1.2.2.1): своя серая плашка одной высоты у всех проектов,
 * обложка ровно в высоту трёх строк текста. Строки: значок типа и название, справа — сколько дней до дедлайна;
 * чтецы; бейджи подстатуса и заметки. У правого края — отметки точками столбиком: справа отступ 2 px, до точки 4 px
 * (концепт list-flag-dots 1.4). Сколько бы строк ни было (две — без бейджей), они стоят по центру плашки.
 * Видимые зазоры между строками подобраны на глаз (2026-10-14): от низа значка типа и хвостов «д», «р», «у» до верха заглавных
 * у чтецов — 5,5 px, от линии букв чтецов до заливки бейджа — 7,5 px. Ровно поровну (по 6,5) чтецы казались ниже середины:
 * они заходят на 1 px под строку названия, над бейджами — 3 px.
 */
export const ProjectListRow = memo(function ProjectListRow({
  project,
  selected,
  onSelect,
  withStage,
  flags = true,
  className,
}: {
  project: Project;
  selected: boolean;
  onSelect: (projectId: string) => void;
  /** Без точек отметок — карточка в поле ввода чата, на их месте кнопка «Убрать» */
  flags?: boolean;
  className?: string;
} & ListRowOptions) {
  const view = getListRowView(project, { withStage });
  const itemMotion = useListItemMotion();

  return (
    <motion.button
      {...itemMotion}
      // Проект, сменивший этап или место в сортировке, переезжает на новое место (AnimatedList)
      data-flip-id={`project-${project.id}`}
      type="button"
      data-project-id={project.id}
      aria-current={selected ? 'true' : undefined}
      onClick={() => onSelect(project.id)}
      {...projectChatDragProps(project)}
      className={cn(
        'nd:group/row nd:row-hit nd:mb-(--row-gap) nd:flex nd:h-[4.625rem] nd:w-full nd:items-center nd:gap-3 nd:rounded-[0.75rem] nd:bg-row nd:pr-0.5 nd:pl-2.5 nd:text-left nd:press-wide nd:focus-ring nd:[--row-gap:0.25rem] nd:last:mb-0 nd:hover:bg-row-hover',
        selected && 'nd:bg-row-selected nd:hover:bg-row-selected',
        project.is_project_highlighted && HIGHLIGHTED[selected ? 'selected' : 'idle'],
        className
      )}
    >
      {/* 58 px = строки 20 − 1 + 18 + 3 + 18: обложка не выше и не ниже трёх строк */}
      <ProjectCover asta={project.asta} name={project.name} className="nd:w-[3.625rem]" />
      <span className="nd:flex nd:min-w-0 nd:flex-1 nd:flex-col nd:justify-center">
        <span className="nd:flex nd:min-w-0 nd:items-center nd:gap-1.5">
          {view.typeMark ? <ProjectTypeMark mark={view.typeMark} /> : null}
          <Tooltip label={project.name} truncated>
            <span className="nd:truncate nd:text-sm nd:leading-5 nd:font-medium">{project.name}</span>
          </Tooltip>
          {view.deadline ? <DeadlineLeft deadline={view.deadline} /> : null}
        </span>
        <span className="nd:-mt-px nd:truncate nd:text-xs nd:leading-[1.125rem] nd:text-muted-foreground">
          {view.readers || <EmptyValue />}
        </span>
        {view.chip || view.note ? (
          <span className="nd:mt-0.75 nd:flex nd:min-w-0 nd:items-center nd:gap-1.5">
            {/* Рядом с заметкой подстатус занимает не больше половины с небольшим; один — всю строку */}
            {view.chip ? <StageChip stage={view.stage} label={view.chip} className={view.note ? 'nd:max-w-[55%]' : 'nd:max-w-full'} /> : null}
            {view.note ? <NameNote note={view.note} size="sm" /> : null}
          </span>
        ) : null}
      </span>
      {/* Вполсилы, чтобы не спорить с названием; у выбранной строки, под курсором и в фокусе — в полную */}
      {flags ? (
        <ProjectFlagDots
          project={project}
          className={cn(
            'nd:transition-opacity nd:duration-150 nd:group-hover/row:opacity-100 nd:group-focus-visible/row:opacity-100',
            selected ? 'nd:opacity-100' : 'nd:opacity-50'
          )}
        />
      ) : null}
    </motion.button>
  );
});

/**
 * Карточка проекта, которого нет на руках, — только обложка-буква и название (концепт project-to-chat 4.1).
 * unavailable — проект удалён или к нему нет доступа: вся карточка бледная, не нажимается. Без него — проект ещё грузится.
 */
export function ProjectListRowStub({ name, unavailable }: { name: string; unavailable: boolean }) {
  return (
    <div
      aria-disabled={unavailable || undefined}
      className={cn(
        'nd:flex nd:h-[4.625rem] nd:w-full nd:items-center nd:gap-3 nd:rounded-[0.75rem] nd:bg-row nd:pr-0.5 nd:pl-2.5',
        unavailable && 'nd:opacity-45'
      )}
    >
      <ProjectCover asta={null} name={name} className="nd:w-[3.625rem]" />
      <span className="nd:min-w-0 nd:flex-1 nd:truncate nd:text-sm nd:leading-5 nd:font-medium">{name}</span>
    </div>
  );
}

/**
 * «5 дней» / «−2 дня» / «сегодня» справа от названия; у готовых — дата. Срок этапа — с квадратиком этапа перед остатком
 * (концепт list-stage-deadline 3.1). Название срока и полная дата — в подсказке.
 */
function DeadlineLeft({ deadline }: { deadline: ListDeadlineView }) {
  return (
    <Tooltip label={deadlineTooltipLabel(deadline)}>
      <span
        data-deadline-stage={deadline.stage ?? undefined}
        className={cn(
          'nd:ml-auto nd:flex nd:shrink-0 nd:items-center nd:gap-1.5 nd:pl-2 nd:text-xs nd:leading-5 nd:font-semibold nd:whitespace-nowrap nd:tabular-nums',
          DEADLINE_TEXT_TONE[deadline.tone]
        )}
      >
        {deadline.stage ? <StageSwatch status={deadline.stage} /> : null}
        <span>{deadline.tone === 'done' ? deadline.shortDate : deadlineLeftLabel(deadline)}</span>
      </span>
    </Tooltip>
  );
}
